import React, { useMemo } from 'react';
import Plot from 'react-plotly.js';

/**
 * 지반응답해석 상세 화면에서 쓰는 차트 모음.
 *
 * 심도 축은 모두 위에서 아래로 증가하도록 반전한다(지반 단면 관례).
 * 색상은 플랫폼 디자인 시스템(primary #2563eb)을 따른다.
 */

const PRIMARY = '#2563eb';
const DANGER = '#ef4444';
const GREEN = '#10b981';
const PURPLE = '#7c3aed';

const BASE_LAYOUT = {
  margin: { l: 56, r: 16, t: 12, b: 44 },
  font: { family: 'Pretendard, -apple-system, sans-serif', size: 11, color: '#374151' },
  paper_bgcolor: 'white',
  plot_bgcolor: 'white',
  showlegend: false,
  hovermode: 'closest',
};

const AXIS = {
  gridcolor: '#f3f4f6',
  zerolinecolor: '#e5e7eb',
  linecolor: '#e5e7eb',
  ticks: 'outside',
  tickcolor: '#e5e7eb',
};

const CONFIG = { displayModeBar: false, responsive: true };

/** 토질명 → 주상도 색상. 미정의 토질은 회색으로 떨어뜨린다. */
export const SOIL_COLORS = {
  모래: '#e0c878',
  실트: '#a3b06a',
  점토: '#b08968',
  자갈: '#d99a4e',
  유기질토: '#7f6a53',
  풍화암: '#7d8a9c',
  연암: '#6b7684',
  보통암: '#5a6472',
  경암: '#4b5563',
  극경암: '#3f4653',
};

export const soilColor = (soil) => {
  const key = String(soil || '').split('(')[0];
  return SOIL_COLORS[key] || '#9ca3af';
};

/** 입력 · 지표면 가속도 시간이력 오버레이. */
export const AccelerationHistoryChart = ({ timeHistory, height = 230 }) => {
  const data = useMemo(() => {
    if (!timeHistory) return [];
    const t = (arr, dt) => arr.map((_, i) => +(i * dt).toFixed(4));
    // 지표면 파형이 진폭이 크므로 먼저(뒤에) 그리고, 입력 파형을 위에 겹쳐
    // 두 계열이 모두 보이게 한다. 순서를 바꾸면 입력이 완전히 가려진다.
    return [
      {
        x: t(timeHistory.surface, timeHistory.dt_surface),
        y: timeHistory.surface,
        type: 'scatter',
        mode: 'lines',
        name: '지표면 가속도',
        line: { color: DANGER, width: 1 },
        opacity: 0.55,
      },
      {
        x: t(timeHistory.input, timeHistory.dt_input),
        y: timeHistory.input,
        type: 'scatter',
        mode: 'lines',
        name: '입력 가속도',
        line: { color: PRIMARY, width: 0.9 },
      },
    ];
  }, [timeHistory]);

  if (!timeHistory) return null;

  return (
    <Plot
      data={data}
      layout={{
        ...BASE_LAYOUT,
        height,
        showlegend: true,
        legend: { orientation: 'h', x: 1, xanchor: 'right', y: 1.18, font: { size: 10 } },
        xaxis: { ...AXIS, title: { text: '시간 (s)', font: { size: 10 } } },
        yaxis: { ...AXIS, title: { text: `가속도 (${timeHistory.unit || 'g'})`, font: { size: 10 } } },
      }}
      config={CONFIG}
      style={{ width: '100%' }}
      useResizeHandler
    />
  );
};

/** 심도별 액상화안전율 FL. FL=1.0 기준선을 함께 그린다. */
export const FLProfileChart = ({ profile, height = 420 }) => {
  if (!profile?.length) return null;
  const maxDepth = Math.max(...profile.map((r) => r.depth));
  const maxFl = Math.max(2.2, ...profile.map((r) => r.fl));

  return (
    <Plot
      data={[
        {
          x: profile.map((r) => r.fl),
          y: profile.map((r) => r.depth),
          type: 'scatter',
          mode: 'lines+markers',
          line: { color: PRIMARY, width: 2 },
          marker: { size: 6, color: PRIMARY },
          hovertemplate: '심도 %{y:.2f} m<br>FL %{x:.2f}<extra></extra>',
        },
      ]}
      layout={{
        ...BASE_LAYOUT,
        height,
        xaxis: { ...AXIS, title: { text: 'FL', font: { size: 10 } }, range: [0, maxFl] },
        yaxis: {
          ...AXIS,
          title: { text: '심도 (m)', font: { size: 10 } },
          autorange: 'reversed',
          range: [maxDepth, 0],
        },
        shapes: [
          {
            type: 'line',
            x0: 1, x1: 1, y0: 0, y1: maxDepth,
            line: { color: DANGER, width: 1.5, dash: 'dot' },
          },
        ],
        annotations: [
          {
            x: 1, y: 0, xanchor: 'left', yanchor: 'bottom',
            text: 'FL = 1', showarrow: false,
            font: { size: 10, color: DANGER },
          },
        ],
      }}
      config={CONFIG}
      style={{ width: '100%' }}
      useResizeHandler
    />
  );
};

/** 심도별 CSR / CRR 대비. 두 곡선이 교차하는 구간이 액상화 우려 구간이다. */
export const CSRCRRProfileChart = ({ profile, height = 420 }) => {
  if (!profile?.length) return null;
  const maxDepth = Math.max(...profile.map((r) => r.depth));

  return (
    <Plot
      data={[
        {
          x: profile.map((r) => r.csr),
          y: profile.map((r) => r.depth),
          type: 'scatter', mode: 'lines+markers', name: 'CSR',
          line: { color: PURPLE, width: 2 }, marker: { size: 5, color: PURPLE },
          hovertemplate: '심도 %{y:.2f} m<br>CSR %{x:.3f}<extra></extra>',
        },
        {
          x: profile.map((r) => r.crr),
          y: profile.map((r) => r.depth),
          type: 'scatter', mode: 'lines+markers', name: 'CRR',
          line: { color: GREEN, width: 2 }, marker: { size: 5, color: GREEN },
          hovertemplate: '심도 %{y:.2f} m<br>CRR %{x:.3f}<extra></extra>',
        },
      ]}
      layout={{
        ...BASE_LAYOUT,
        height,
        showlegend: true,
        legend: { orientation: 'h', x: 1, xanchor: 'right', y: 1.1, font: { size: 10 } },
        xaxis: { ...AXIS, title: { text: 'CSR / CRR', font: { size: 10 } } },
        yaxis: {
          ...AXIS,
          title: { text: '심도 (m)', font: { size: 10 } },
          autorange: 'reversed',
          range: [maxDepth, 0],
        },
      }}
      config={CONFIG}
      style={{ width: '100%' }}
      useResizeHandler
    />
  );
};

/** 시추주상도 — 지층을 색 블록으로 쌓고 지하수위선을 표시한다. */
export const BoreholeLogChart = ({ layers, waterLevel, height = 460 }) => {
  if (!layers?.length) return null;
  const totalDepth = Math.max(...layers.map((l) => l.bottom_depth));

  const shapes = layers.map((l) => ({
    type: 'rect',
    xref: 'x', yref: 'y',
    x0: 0.15, x1: 0.85,
    y0: l.top_depth, y1: l.bottom_depth,
    fillcolor: soilColor(l.soil),
    line: { color: '#ffffff', width: 1 },
  }));

  if (waterLevel != null && waterLevel >= 0 && waterLevel <= totalDepth) {
    shapes.push({
      type: 'line',
      x0: 0.05, x1: 0.95, y0: waterLevel, y1: waterLevel,
      line: { color: '#0ea5e9', width: 1.5, dash: 'dash' },
    });
  }

  const annotations = layers.map((l) => ({
    x: 0.5,
    y: (l.top_depth + l.bottom_depth) / 2,
    text: l.soil,
    showarrow: false,
    font: { size: 10, color: '#ffffff' },
  }));

  if (waterLevel != null && waterLevel <= totalDepth) {
    annotations.push({
      x: 0.05, y: waterLevel, xanchor: 'left', yanchor: 'bottom',
      text: `GWL ${waterLevel}m`, showarrow: false,
      font: { size: 9, color: '#0284c7' },
    });
  }

  return (
    <Plot
      data={[{ x: [0], y: [0], type: 'scatter', mode: 'markers', marker: { opacity: 0 } }]}
      layout={{
        ...BASE_LAYOUT,
        height,
        margin: { l: 48, r: 8, t: 8, b: 24 },
        xaxis: { range: [0, 1], visible: false, fixedrange: true },
        yaxis: {
          ...AXIS,
          title: { text: '심도 (m)', font: { size: 10 } },
          range: [totalDepth, 0],
          fixedrange: true,
        },
        shapes,
        annotations,
      }}
      config={CONFIG}
      style={{ width: '100%' }}
      useResizeHandler
    />
  );
};

/** 입력 지진파 파형 미리보기 (선택 화면용). */
export const InputMotionPreview = ({ timeHistory, height = 150 }) => {
  if (!timeHistory?.input?.length) return null;
  return (
    <Plot
      data={[
        {
          x: timeHistory.input.map((_, i) => +(i * timeHistory.dt_input).toFixed(4)),
          y: timeHistory.input,
          type: 'scatter', mode: 'lines',
          line: { color: PRIMARY, width: 1 },
        },
      ]}
      layout={{
        ...BASE_LAYOUT,
        height,
        margin: { l: 48, r: 12, t: 8, b: 32 },
        xaxis: { ...AXIS, title: { text: '시간 (s)', font: { size: 9 } } },
        yaxis: { ...AXIS, title: { text: 'acc (g)', font: { size: 9 } } },
      }}
      config={CONFIG}
      style={{ width: '100%' }}
      useResizeHandler
    />
  );
};
